/**
 * Module-level pool for the three User Metrics batch workers.
 *
 * WHY THIS EXISTS (ordering, not algorithms):
 * The worker chunk is not small — UserMetricsWorker imports UserMetricsWorkerFunctions
 * and UserMetricsWorkerUtils, both of which pull in moment. Fetching, parsing and
 * compiling that chunk takes real time, and messages posted to a worker whose script is
 * still loading are BUFFERED rather than dropped. So if the workers are not created
 * until the component that uses them renders, the whole script-load cost lands on the
 * critical path *after* the S3 fetch instead of overlapping it.
 *
 * Owning the workers at module scope lets `ensureWorkersStarted()` be called from
 * wherever the S3 fetch begins, so the chunk downloads concurrently with the network
 * wait it can hide behind. This mirrors the existing lazyWithPreload /
 * ROUTE_PRELOAD_MAP pattern in lazyRoutes.ts — warming a chunk before it is needed.
 *
 * WHY NOT IN THE COMPONENT:
 * The previous shape created the workers in the render body and terminated them on
 * unmount, nulling the refs so a remount would rebuild them. That nulling was a
 * correct fix for a real bug (React StrictMode double-mounts in dev; refs survive
 * unmount; a TERMINATED worker silently swallows every message, which surfaces as an
 * infinite loading spinner). But it also means StrictMode pays the full script-load
 * cost twice, and every navigation away and back pays it again.
 *
 * A module-level pool fixes that churn structurally: the workers outlive the component,
 * so there is nothing to rebuild. The terminated-worker hazard is avoided by simply
 * never terminating — see `terminateWorkers` below for why that is the right default.
 */
import { logger } from 'src/logger';
// Type-only: this module is reachable from the eager app shell (it is what warms the
// worker chunk), so it must not pull UserMetricsInterfaces' runtime imports in with it.
import type { WorkerResponse } from '../UserMetricsInterfaces';

/**
 * One entry per concurrent batch worker; batches A/B/C are dispatched one per worker.
 * The labels themselves are only used for logging, so a crash can be attributed to a
 * specific worker.
 */
const WORKER_LABELS = ['A', 'B', 'C'];

let workers: Worker[] | null = null;

/**
 * Current message handler. Held in a module-level slot (rather than bound at creation)
 * so the owning component can swap in a fresh closure on every render without needing
 * to recreate the workers — the same reason the component previously used a ref.
 */
let messageHandler: (data: WorkerResponse) => void = () => {};

/** Invoked when a worker fires `onerror`, so the UI can surface a failure state. */
let errorHandler: (label: string) => void = () => {};

/**
 * Monotonic dispatch counter, scoped to the pool rather than to the consuming component.
 *
 * It has to outlive the component for the same reason the workers do: an in-flight reply
 * from a previous mount is still coming, and if the counter restarted at zero on remount
 * that reply's id could collide with a fresh dispatch's and be accepted as current.
 */
let dispatchCounter = 0;

/** Returns the next dispatch id. Call once per dispatch, before building the batches. */
export const nextDispatchId = (): number => ++dispatchCounter;

/**
 * Creates the worker pool if it does not already exist. Safe and cheap to call
 * repeatedly — subsequent calls are a no-op.
 *
 * Call this as early as the worker output is known to be needed; the earliest useful
 * point is alongside the S3 fetch that produces their input, so the two overlap.
 */
export const ensureWorkersStarted = (): void => {
  if (workers) {
    return;
  }

  workers = WORKER_LABELS.map((label) => {
    // jsdom has no Worker constructor, so a unit test that renders the consuming
    // component would otherwise throw on import. Substitute an inert worker instead:
    // dispatch is silently dropped, which is exactly what a test that never asserts on
    // worker output wants, and it keeps the pool at a fixed size so callers below never
    // have to null-check.
    if (typeof Worker === 'undefined') {
      logger.warn(`[Worker ${label}] Worker is unavailable in this environment — using an inert stand-in`);
      return createInertWorker();
    }

    // Keep the new URL literal inline so Vite emits the chunk; the worker is an ES module in both dev and build.
    const worker = new Worker(new URL('./UserMetricsWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => messageHandler(e.data);
    worker.onerror = (e) => {
      logger.error(`[Worker ${label}] Unhandled error — worker may have crashed`, e);
      errorHandler(label);
    };
    return worker;
  });
};

/**
 * Stand-in used only where `Worker` does not exist (jsdom). Implements the surface this
 * feature actually touches — postMessage and terminate — and does nothing with either.
 */
const createInertWorker = (): Worker =>
  ({
    postMessage: () => {},
    terminate: () => {}
  } as unknown as Worker);

/**
 * Returns the pooled workers, starting them first if necessary.
 *
 * Always returns one live worker per entry in WORKER_LABELS, so callers never have to
 * null-check or guard against a half-initialised pool.
 */
export const getWorkers = (): Worker[] => {
  ensureWorkersStarted();
  // Non-null: ensureWorkersStarted has just guaranteed the pool exists.
  return workers as Worker[];
};

/**
 * Registers the callback invoked for every message from any worker in the pool.
 * Replaces any previously registered handler.
 */
export const setWorkerMessageHandler = (handler: (data: WorkerResponse) => void): void => {
  messageHandler = handler;
};

/**
 * Registers the callback invoked when a worker reports an unhandled error.
 * Receives the worker's label ('A' | 'B' | 'C') for diagnostics.
 */
export const setWorkerErrorHandler = (handler: (label: string) => void): void => {
  errorHandler = handler;
};

/**
 * Terminates the pool and clears it so a later `ensureWorkersStarted()` rebuilds it.
 *
 * DELIBERATELY NOT CALLED ON COMPONENT UNMOUNT. Three idle workers cost far less than
 * re-paying the script-load on every navigation, and terminating them is precisely the
 * hazard this module exists to avoid: a terminated worker accepts postMessage without
 * error and never replies, so the pending-task counter never reaches zero and the page
 * shows a permanent spinner. Only call this if the pool genuinely needs to be torn down
 * (e.g. a test needs a clean slate), and only where no dispatch is in flight.
 */
export const terminateWorkers = (): void => {
  workers?.forEach((worker) => worker.terminate());
  workers = null;
};
